import React, { useState, useEffect, useCallback } from 'react';
import { NewcomerForm } from '../components/newcomers/NewcomerForm';
import { NewcomerList } from '../components/newcomers/NewcomerList';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { FaUserPlus, FaLock } from 'react-icons/fa';
import { Modal } from '../components/common/Modal';
import { newcomerService } from '../services/newcomer.service';
import type { Newcomer } from '../types/newcomer';
import { usePermissions } from '../hooks/usePermissions';

export const NewcomerPage: React.FC = () => {
  const { canWrite, canDelete } = usePermissions();
  const [newcomers, setNewcomers] = useState<Newcomer[]>([]);
  const [editingNewcomer, setEditingNewcomer] = useState<Newcomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const canEditNewcomers = canWrite('newcomers');
  const canDeleteNewcomers = canDelete('newcomers');

  const fetchNewcomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const newcomerData = await newcomerService.getAllNewcomers();
      setNewcomers(newcomerData);
    } catch (err) {
      console.error(err);
      setError('새가족 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      // finally 블록을 올바르게 수정합니다.
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNewcomers();
  }, [fetchNewcomers]);
  
  const handleFormSuccess = () => {
    fetchNewcomers();
    setIsModalOpen(false);
  };
  
  // '새가족 추가' 버튼 클릭 시
  const handleAddNew = () => {
    if (!canEditNewcomers) {
      alert("추가 권한이 없습니다.");
      return;
    }
    
    setEditingNewcomer(null); // 수정 모드가 아님을 명시
    setIsModalOpen(true);
  };

  // 목록에서 '수정' 버튼 클릭 시
  const handleEdit = (newcomer: Newcomer) => {
    if (!canEditNewcomers) {
      alert("수정 권한이 없습니다.");
      return;
    }
    
    setEditingNewcomer(newcomer); // 수정할 데이터를 state에 저장
    setIsModalOpen(true);        // 모달창 열기
  };

  const handleDelete = async (id: string) => {
    if (!canDeleteNewcomers) {
      alert("삭제 권한이 없습니다.");
      return;
    }
    
    if (window.confirm("정말로 이 새가족 정보를 삭제하시겠습니까?")) {
      try {
        await newcomerService.deleteNewcomer(id);
        alert("삭제되었습니다.");
        fetchNewcomers(); // 목록 새로고침
      } catch (err) {
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const handleAssign = (id: string) => {
    // TODO: 담당자 지정 기능 구현 후 연결
    alert(`ID: ${id} 담당자 지정 기능은 구현 예정입니다.`);
  };

  const renderContent = () => {
    if (loading) return <div className="py-10"><LoadingSpinner /></div>;
    if (error) return <p className="text-center text-red-500 bg-red-100 p-4 rounded-md">{error}</p>;
    
    return <NewcomerList 
              newcomers={newcomers} 
              onEdit={canEditNewcomers ? handleEdit : undefined} 
              onDelete={canDeleteNewcomers ? handleDelete : undefined} 
              onAssign={handleAssign}
            />;
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div className="flex items-center">
          <FaUserPlus className="text-3xl text-gray-600 dark:text-gray-200 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-600 dark:text-gray-200">새가족관리</h1>
            <p className="ml-1 mt-1 text-md text-gray-500 dark:text-gray-400">새롭게 방문한 분들의 현황을 관리합니다.</p>
          </div>
        </div>
        {canEditNewcomers ? (
          <button onClick={handleAddNew} className="px-4 py-2 bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap">
            + 새가족추가
          </button>
        ) : (
          <div className="flex items-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg">
            <FaLock className="mr-2 h-4 w-4" />
            읽기 전용
          </div>
        )}
      </div>
      <div className="mt-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          {renderContent()}
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingNewcomer ? "새가족 정보 수정" : "새가족 등록"}>
        <NewcomerForm onSuccess={handleFormSuccess} editingNewcomer={editingNewcomer} readOnly={!canEditNewcomers} />
      </Modal>
    </div>
  );
};
